import { DocumentNode, useMutation } from '@apollo/client';
import { Arrow } from '@otp/icons';
import {
  BottomNavigation,
  Button,
  Controller,
  FileElement,
  FormDirtyModal,
  Input,
  PageModal,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { AttachmentsField } from 'src/components/common/resources/form/attachments-field';
import {
  mapFileAssetsToForm,
  mapFileAssetsToRequest,
} from 'src/components/common/resources/form/utils';
import {
  CreateIssueTicketDocument,
  FileAsset,
  IssueTicketImportance,
  IssueTicketStatus,
  Maybe,
  StoreModel,
  Tag,
  TagEntityType,
  UpdateIssueTicketDocument,
  User,
} from 'src/graphql/generated/graphql';
import { ImportanceField } from './fields/importance';
import { useState } from 'react';
import { StatusField } from './fields/status';
import { TagsField } from 'src/components/common/form/fields/tags';
import { UsersServerSideField } from 'src/components/common/form/fields';
import { StoresServerSideField } from 'src/components/common/form/fields/stores-serverside';

type IssueTicket = {
  id?: string;
  name: string;
  description: string;
  importance: IssueTicketImportance;
  tags: Partial<Tag>[];
  status: IssueTicketStatus;
  stores: Partial<StoreModel>[];
  assignedTo: Maybe<Partial<User>>;
  questionIds: string[];
  fileAssets: FileAsset[];
};

type Form = {
  name: string;
  description: string;
  importance: IssueTicketImportance;
  status: IssueTicketStatus;
  tagIds: string[];
  storeIds: string[];
  assignedToUserId: string | null;
  questionIds: string[];
  fileAssets: FileElement[];
};

type IssueTicketFormProps = {
  issueTicket?: Partial<IssueTicket>;
  defaultStoreIds?: string[];
  defaultQuestionIds?: string[];
  disabledFields?: {
    storeIds?: boolean;
  };
  refetchQueries?: DocumentNode[];
  onCancel: () => void;
  onSave: () => void;
};

export const IssueTicketForm = ({
  issueTicket,
  defaultStoreIds = [],
  defaultQuestionIds = [],
  disabledFields = {},
  refetchQueries = [],
  onCancel,
  onSave,
}: IssueTicketFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'issue.ticket.form',
  });

  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);

  const [createIssueTicket, { loading: loadingCreate }] = useMutation(
    CreateIssueTicketDocument,
  );
  const [updateIssueTicket, { loading: loadingUpdate }] = useMutation(
    UpdateIssueTicketDocument,
  );
  const loading = loadingCreate || loadingUpdate;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm<Form>({
    defaultValues: {
      name: issueTicket?.name || '',
      description: issueTicket?.description || '',
      importance: issueTicket?.importance || IssueTicketImportance.Low,
      status: issueTicket?.status || IssueTicketStatus.Open,
      tagIds: issueTicket?.tags?.map((tag) => tag.id) || [],
      storeIds:
        issueTicket?.stores?.map((store) => store.id) || defaultStoreIds,
      assignedToUserId: issueTicket?.assignedTo?.id || null,
      questionIds: issueTicket?.questionIds || defaultQuestionIds,
      fileAssets: mapFileAssetsToForm(issueTicket?.fileAssets || []),
    },
  });

  const onClose = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onCancel();
    }
  };

  const onSubmit = async (data: Form) => {
    const { fileAssets, ...rest } = data;
    const handledFileAssets = mapFileAssetsToRequest(fileAssets);

    const formData = {
      ...rest,
      fileAssets: handledFileAssets,
    };

    if (issueTicket?.id) {
      await updateIssueTicket({
        variables: {
          id: issueTicket.id,
          data: formData,
        },
        refetchQueries,
      });
    } else {
      await createIssueTicket({
        variables: {
          data: formData,
        },
        refetchQueries,
      });
    }

    onSave();
  };

  const title = issueTicket?.id ? t('title-edit') : t('title-create');

  return (
    <PageModal open={true} onClose={onClose} title={title}>
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  label={t('fields.name')}
                  placeholder={t('fields.name')}
                  error={!!errors.name}
                  required={true}
                  {...field}
                  defaultValue={field.value}
                />
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="description"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.description ? 'red' : ''}
                  >
                    {t('fields.description')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="importance"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.importance ? 'red' : ''}
                  >
                    {t('fields.importance')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <ImportanceField
                    field={field}
                    hasError={!!errors.importance}
                  />
                </div>
              )}
            />
          </div>
          {issueTicket?.id && (
            <div className="mb-4">
              <Controller
                name="status"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div>
                    <Typography
                      className="mb-2"
                      variant="subtitle1"
                      color={!!errors.status ? 'red' : ''}
                    >
                      {t('fields.status')}
                      <span className="text-red-500"> *</span>
                    </Typography>
                    <StatusField field={field} hasError={!!errors.status} />
                  </div>
                )}
              />
            </div>
          )}
          <div className="mb-4">
            <Controller
              name="tagIds"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('fields.tags')}
                  </Typography>
                  <TagsField
                    field={field}
                    entityType={TagEntityType.IssueTicket}
                  />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="assignedToUserId"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('fields.assigned-to')}
                  </Typography>
                  <UsersServerSideField field={field} />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="storeIds"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.storeIds ? 'red' : ''}
                  >
                    {t('fields.stores')}
                    <span className="text-red-500"> *</span>
                  </Typography>
                  <StoresServerSideField
                    field={field}
                    disabled={disabledFields.storeIds}
                    useCheckboxOption={true}
                    multiple={true}
                  />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Controller
              name="fileAssets"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div>
                  <Typography className="mb-2" variant="subtitle1">
                    {t('fields.my-attachments').toUpperCase()}
                  </Typography>
                  <AttachmentsField
                    value={field.value}
                    onChange={(files) => {
                      return setValue(field.name, files, {
                        shouldDirty: true,
                      });
                    }}
                    reverseDirection={true}
                    buttonContent={t('fields.my-attachments-button')}
                    buttonFullWidth={true}
                    fileDescription={false}
                    fileAccept={['video/*', 'image/*']}
                  />
                </div>
              )}
            />
          </div>
          <div className="mb-4">
            <Typography variant="subtitle2" className="mb-2">
              <span className="text-red-500">* </span>
              {t('required-fields')}
            </Typography>
          </div>
          <BottomNavigation>
            <Button variant="outlined" className="grow" onClick={onClose}>
              <Arrow /> &nbsp; {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="grow"
              disabled={loading}
            >
              {t('submit')}
            </Button>
          </BottomNavigation>
        </form>
      </div>
      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={onCancel}
      />
    </PageModal>
  );
};
